// A slides board: a deck the user and their agents write together, presented full screen and shared by link. Each
// slide is an entry (its layout, its title, its text as the doc editor's document, its notes, a picture or a widget),
// in the order of the entries' place, as pages are; the deck's style is one more entry, the board's "deck". Light on
// purpose: the board's main code and the server read it; Markdown in and out is shared/slidesmd.ts.
//
// A slide is designed at 1600 by 900 and drawn scaled to wherever it shows (a thumbnail, the editor, full screen, a
// printed page), so one type scale serves every size. Its text shrinks to fit its box, as Keynote and Google Slides
// do, never past the slide's edge: the browser measures it, and the server estimates it (estimateFit) to tell an agent
// which slides it wrote too much on.

import { cleanAdvance, cleanBuilds, isTransition, type Build, type Transition } from "./slideMotion";
import { arrivedButton } from "./brief";
import { MAX_LEVELS, routeLines } from "./slidePlace";
import { cleanTitleRuns } from "./slideTitle";
import { applyBoxSet, applyPlaceSet, boxLimits, boxWords, cleanBg, cleanColor, cleanBoxes, cleanPlace, type SlideBg, type SlideBox, type SlidePlace } from "./slideDesign";
import { cleanPictureRef, type PictureRef } from "./imageInfo";
import { ThemeRefused, cleanDeckTheme, cleanLayouts, type DeckTheme, type SavedLayout } from "./deckTheme";
import type { DocBody, DocMark, DocNode } from "./docblocks";
import { cleanSectionName } from "./slideDeck";
import { cleanMaster, type DeckMaster } from "./slideMaster";
import type { Entry } from "./types";

// ---------------------------------------------------------------------------
// layouts

export type SlideLayout = "title" | "section" | "text" | "two" | "big" | "quote" | "picture" | "full" | "widget" | "blank";

/** What a layout shows of a slide: the fields a person types into on it. */
export type SlideField = "title" | "body" | "body2" | "figure" | "cite" | "image" | "widget";

export interface LayoutSpec {
  id: SlideLayout;
  /** its name in the layout strip */
  name: string;
  /** what an agent writes in `<!-- layout: … -->` */
  hint: string;
  /** other words for it, as Marp, Slidev and people write them */
  aliases: string[];
  uses: SlideField[];
}

export const LAYOUTS: LayoutSpec[] = [
  { id: "title", name: "Title", hint: "title", aliases: ["cover", "intro", "end", "lead", "title-slide", "opening"], uses: ["title", "body"] },
  { id: "section", name: "Section", hint: "section", aliases: ["statement", "divider", "chapter", "header", "center"], uses: ["title", "body"] },
  { id: "text", name: "Title and text", hint: "text", aliases: ["default", "content", "bullets", "title-and-text", "list", "body"], uses: ["title", "body"] },
  { id: "two", name: "Two columns", hint: "two-columns", aliases: ["two", "two-cols", "two-cols-header", "columns", "comparison", "two-column"], uses: ["title", "body", "body2"] },
  { id: "big", name: "Big number", hint: "big-number", aliases: ["big", "fact", "number", "stat", "metric", "kpi", "figure"], uses: ["title", "figure", "body"] },
  { id: "quote", name: "Quote", hint: "quote", aliases: ["testimonial", "quotation"], uses: ["title", "body", "cite"] },
  { id: "picture", name: "Picture with text", hint: "picture", aliases: ["image-right", "image-left", "picture-with-text", "image-text", "media", "split"], uses: ["title", "body", "image"] },
  { id: "full", name: "Full picture", hint: "full-picture", aliases: ["image", "full", "cover-image", "photo", "background", "bg", "full-image"], uses: ["title", "body", "image"] },
  { id: "widget", name: "Widget", hint: "widget", aliases: ["live", "chart", "live-widget"], uses: ["title", "widget"] },
  { id: "blank", name: "Blank", hint: "blank", aliases: ["empty", "none"], uses: [] },
];

export const LAYOUT: Record<SlideLayout, LayoutSpec> = Object.fromEntries(LAYOUTS.map((l) => [l.id, l])) as Record<SlideLayout, LayoutSpec>;

export function isLayout(x: unknown): x is SlideLayout {
  return typeof x === "string" && Object.hasOwn(LAYOUT, x);
}

/** A layout as written in a hint ("big-number", "Two columns", "two-cols"), or null for none the board has. */
export function layoutFromHint(raw: unknown): SlideLayout | null {
  if (typeof raw !== "string") return null;
  const k = raw.trim().toLowerCase().replace(/[\s_]+/g, "-");
  if (!k) return null;
  for (const l of LAYOUTS) if (l.id === k || l.hint === k || l.name.toLowerCase().replace(/\s+/g, "-") === k || l.aliases.includes(k)) return l.id;
  return null;
}

// ---------------------------------------------------------------------------
// deck styles

export type DeckStyle = "board" | "paper" | "night" | "bold" | "editorial";

export interface DeckStyleSpec {
  id: DeckStyle;
  name: string;
  /** a few words under its name */
  blurb: string;
  /** its own accent, unless the user picks another; null: the board's theme accent */
  accent: string | null;
}

export const DECK_STYLES: DeckStyleSpec[] = [
  { id: "board", name: "Board", blurb: "This board's colors and type", accent: null },
  { id: "paper", name: "Paper", blurb: "Warm white, quiet ink", accent: "#C2410C" },
  { id: "night", name: "Night", blurb: "Deep charcoal, soft light", accent: "#E9B44C" },
  { id: "bold", name: "Bold", blurb: "Big color on the key slides", accent: "#2F54EB" },
  { id: "editorial", name: "Editorial", blurb: "Serif titles on cream", accent: "#9A3B2E" },
];

export const DECK_STYLE: Record<DeckStyle, DeckStyleSpec> = Object.fromEntries(DECK_STYLES.map((s) => [s.id, s])) as Record<DeckStyle, DeckStyleSpec>;

/** The accents offered beside a style's own: each reads on the light and the dark styles. */
export const ACCENTS = ["#2F54EB", "#0E8A6A", "#C2410C", "#B4235A", "#6E48E0", "#A87B00"];

export function isDeckStyle(x: unknown): x is DeckStyle {
  return typeof x === "string" && Object.hasOwn(DECK_STYLE, x);
}

/** A deck style as written ("Night", "dark", "light"), or null for none. */
export function styleFromHint(raw: unknown): DeckStyle | null {
  if (typeof raw !== "string") return null;
  const k = raw.trim().toLowerCase();
  if (isDeckStyle(k)) return k;
  const alias: Record<string, DeckStyle> = { light: "paper", white: "paper", dark: "night", black: "night", accent: "bold", color: "bold", colour: "bold", serif: "editorial", cream: "editorial", theme: "board", default: "board" };
  return alias[k] ?? null;
}

// ---------------------------------------------------------------------------
// a slide, and the deck, as kept

export interface SlideData {
  layout: SlideLayout;
  /** one to three lines */
  title: string;
  /** its text: the doc editor's document, narrowed to paragraphs and lists (narrowBlocks) */
  body: DocBody | null;
  /** Two columns: the right one */
  body2?: DocBody | null;
  /** Big number: the figure ("$48.2k", "3.2×", "92%") */
  figure?: string;
  /** Quote: who said it */
  cite?: string;
  /** what the speaker says; never on a shared link */
  notes?: string;
  /** a picture the board keeps (Picture with text, Full picture) */
  image?: PictureRef | null;
  /** a widget item on this board (Widget) */
  widget?: string | null;
  /** boxes a person or an agent put on the slide, in layer order (shared/slideDesign.ts) */
  boxes?: SlideBox[];
  /** where the layout's fields sit and how they look on this slide, where changed; absent: the layout's own */
  place?: SlidePlace;
  /** the slide's own background; absent: the deck's */
  bg?: SlideBg | null;
  /** the deck's layout of the user's own it was made from (shared/deckTheme.ts), for its prompts and empty picture places */
  lay?: string;
  /** drawn as it was made, with none of its layout's own marks (the accent's rule): a slide brought from a PowerPoint file (S7) */
  plain?: boolean;
  /** how it comes in when presenting, its own over the deck's (S7, shared/slideMotion.ts) */
  transition?: Transition;
  /** SA: the title's words with single words marked (shared/slideTitle.ts), kept while they are the title's */
  titleRuns?: DocNode[];
  /** what appears on a click when presenting, in order: boxes by id, fields as "f:title" */
  builds?: Build[];
  /** Fixes 8: seconds the slide stays when presenting before the talk moves on by itself, each build first (PowerPoint's After) */
  advance?: number;
  /** SC: the name of the section that starts at this slide (shared/slideDeck.ts) */
  section?: string;
  /** SC: skipped when presenting, on a shared link and in the PDF; kept in the PowerPoint file as hidden */
  hidden?: boolean;
  /** SC: drawn without the master's and its layout's boxes and logo (PowerPoint's Hide Background Graphics) */
  noMaster?: boolean;
}

export interface DeckData {
  style: DeckStyle;
  /** an accent the user picked; null for the style's own */
  accent: string | null;
  /** a theme of the user's own, the deck's own copy of its values; absent: the style's (shared/deckTheme.ts) */
  theme?: DeckTheme | null;
  /** the deck's layouts of the user's own */
  layouts?: SavedLayout[];
  /** how every slide comes in when presenting, unless a slide says its own (S7) */
  transition?: Transition;
  /** SC: the slide master's own pieces and each of the board's layouts' (shared/slideMaster.ts) */
  master?: DeckMaster;
}

export const MAX_TITLE = 240;
export const MAX_FIGURE = 24;
export const MAX_CITE = 160;
export const MAX_NOTES = 20_000;
/** The most slides one deck holds. */
export const MAX_SLIDES = 400;
const ITEM_ID = /^[A-Za-z0-9_-]{4,40}$/;
const HEX = /^#[0-9a-fA-F]{6}$/;

/** A slide's fields refused, with words a person or an agent reads. */
export class SlideRefused extends Error {}

/** A title as kept: up to three lines, each trimmed, and no longer than MAX_TITLE. */
export function cleanTitle(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/\r\n?/g, "\n")
    .replace(/[\t ]+/g, " ")
    .split("\n")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .slice(0, 3)
    .join("\n")
    .slice(0, MAX_TITLE);
}

const oneLine = (raw: unknown, max: number) => (typeof raw === "string" ? raw.replace(/\s+/g, " ").trim().slice(0, max) : "");

/** Notes as kept: the speaker's own lines, without trailing space, up to MAX_NOTES. */
export function cleanNotes(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, MAX_NOTES);
}

const LINK_OK = /^(https?:|mailto:)/i;

/** A run of words with only the marks a slide keeps: bold, italic and links to the web. */
function narrowInline(nodes: DocNode[] | undefined): DocNode[] {
  const out: DocNode[] = [];
  for (const n of nodes ?? []) {
    if (n.type === "hardBreak") out.push({ type: "hardBreak" });
    else if (n.type === "text" && typeof n.text === "string" && n.text) {
      const marks = (n.marks ?? []).flatMap((m): DocMark[] => {
        // SA (Features 4): underline, strikethrough and a colour on single words, as a text box has them
        if (m.type === "bold" || m.type === "italic" || m.type === "underline" || m.type === "strike") return [{ type: m.type }];
        if (m.type === "textStyle") {
          const color = cleanColor(m.attrs?.color);
          return color ? [{ type: "textStyle", attrs: { color } }] : [];
        }
        return m.type === "link" && typeof m.attrs?.href === "string" && LINK_OK.test(m.attrs.href) ? [{ type: "link", attrs: { href: m.attrs.href } }] : [];
      });
      out.push(marks.length ? { type: "text", text: n.text, marks } : { type: "text", text: n.text });
    } else if (n.content) out.push(...narrowInline(n.content));
  }
  return out;
}

const idOf = (n: DocNode) => (typeof n.attrs?.id === "string" && n.attrs.id ? { id: n.attrs.id } : undefined);
const para = (content: DocNode[], src?: DocNode): DocNode => ({ type: "paragraph", ...(src && idOf(src) ? { attrs: idOf(src) } : {}), ...(content.length ? { content } : {}) });
const bold = (nodes: DocNode[]): DocNode[] => nodes.map((n) => (n.type === "text" ? { ...n, marks: [...(n.marks ?? []).filter((m) => m.type !== "bold"), { type: "bold" }] } : n));

/** A list item as a slide keeps it: its words, then any list inside it, two deep at most. */
function narrowItem(item: DocNode, depth: number): DocNode {
  const content: DocNode[] = [];
  for (const c of item.content ?? []) {
    if (c.type === "bulletList" || c.type === "orderedList" || c.type === "taskList") {
      if (depth < MAX_LEVELS) content.push(narrowList(c, depth + 1));
      else for (const it of c.content ?? []) content.push(para(narrowInline(it.content)));
    } else if (c.type === "paragraph" || c.type === "heading") content.push(para(narrowInline(c.content), c));
    else if (c.content) content.push(para(narrowInline(c.content)));
  }
  if (!content.length || content[0].type !== "paragraph") content.unshift(para([]));
  return { type: "listItem", content };
}

function narrowList(list: DocNode, depth: number): DocNode {
  const type = list.type === "orderedList" ? "orderedList" : "bulletList";
  const start = type === "orderedList" && Number(list.attrs?.start) > 1 ? { start: Math.min(9999, Math.round(Number(list.attrs?.start))) } : {};
  const attrs = { ...(idOf(list) ?? {}), ...start };
  return { type, ...(Object.keys(attrs).length ? { attrs } : {}), content: (list.content ?? []).map((it) => narrowItem(it, depth)) };
}

/**
 * A slide's text as the board keeps it: paragraphs and lists (bulleted or numbered, a list inside a list, no deeper),
 * with bold, italic and links. Anything else a page or Markdown could hold reads as words: a heading as a bold line, a
 * quote's or a toggle's lines as lines, a table's rows as lines; a divider, a picture or a widget is left out (a slide
 * holds its picture and its widget in their own places).
 */
export function narrowBlocks(blocks: DocNode[] | undefined): DocNode[] {
  const out: DocNode[] = [];
  for (const b of blocks ?? []) {
    if (!b || typeof b !== "object" || typeof b.type !== "string") continue;
    switch (b.type) {
      case "paragraph":
        out.push(para(narrowInline(b.content), b));
        break;
      case "heading":
        out.push(para(bold(narrowInline(b.content)), b));
        break;
      case "bulletList":
      case "orderedList":
      case "taskList":
        out.push(narrowList(b, 1));
        break;
      case "codeBlock":
        for (const line of (b.content ?? []).map((c) => c.text ?? "").join("").split("\n")) if (line.trim()) out.push(para([{ type: "text", text: line }]));
        break;
      case "table":
        for (const row of b.content ?? []) {
          const cells = (row.content ?? []).map((cell) => narrowInline((cell.content ?? []).flatMap((p) => p.content ?? [])));
          const words: DocNode[] = [];
          cells.forEach((c, i) => {
            if (i && c.length) words.push({ type: "text", text: " · " });
            words.push(...c);
          });
          if (words.length) out.push(para(row === b.content?.[0] ? bold(words) : words));
        }
        break;
      case "blockquote":
      case "details":
      case "detailsContent":
      case "detailsSummary":
        out.push(...narrowBlocks(b.content));
        break;
      default:
        // a divider, a picture, a widget: not text
        break;
    }
  }
  // empty lines at the end are no text
  while (out.length && out[out.length - 1].type === "paragraph" && !out[out.length - 1].content?.length) out.pop();
  return out;
}

/** A slide's text from what was sent: null for none; anything that is not the editor's document is refused. */
export function cleanSlideBody(raw: unknown): DocBody | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object" || Array.isArray(raw) || (raw as DocBody).type !== "doc" || !Array.isArray((raw as DocBody).content)) throw new SlideRefused('A slide\'s text is the editor\'s document: { type: "doc", content: [blocks] }.');
  const content = narrowBlocks((raw as DocBody).content);
  return content.length ? { type: "doc", content } : null;
}

/**
 * A slide as it may be kept. A field sent as null is cleared (an undo sends null for a field the step added), one not
 * sent keeps what the slide had, and one a layout does not show is kept for when the layout comes back.
 */
export function cleanSlide(raw: Record<string, unknown>, before: SlideData | null): SlideData {
  const has = (k: string) => Object.hasOwn(raw, k);
  const layout = isLayout(raw.layout) ? raw.layout : (before?.layout ?? "text");
  const title = has("title") ? cleanTitle(raw.title) : (before?.title ?? "");
  const body = has("body") ? cleanSlideBody(raw.body) : (before?.body ?? null);
  const body2 = has("body2") ? cleanSlideBody(raw.body2) : (before?.body2 ?? null);
  const figure = has("figure") ? oneLine(raw.figure, MAX_FIGURE) : (before?.figure ?? "");
  const cite = has("cite") ? oneLine(raw.cite, MAX_CITE) : (before?.cite ?? "");
  const notes = has("notes") ? cleanNotes(raw.notes) : (before?.notes ?? "");
  const image = has("image") ? cleanPictureRef(raw.image) : cleanPictureRef(before?.image);
  const widget = has("widget") ? (typeof raw.widget === "string" && ITEM_ID.test(raw.widget) ? raw.widget : null) : (before?.widget ?? null);
  // boxes: a whole list replaces them, a patch (boxSet) changes them by id; places likewise, by field (S5)
  let boxes = has("boxes") ? cleanBoxes(raw.boxes) : cleanBoxes(before?.boxes);
  if (has("boxSet")) boxes = applyBoxSet(boxes, raw.boxSet);
  // SA: lines attached to boxes follow them, wherever a slide is written from
  boxes = routeLines(boxes);
  let place = has("place") ? cleanPlace(raw.place) : cleanPlace(before?.place);
  if (has("placeSet")) place = applyPlaceSet(place, raw.placeSet);
  const bg = has("bg") ? cleanBg(raw.bg) : cleanBg(before?.bg);
  const lay = has("lay") ? (typeof raw.lay === "string" && /^L[A-Za-z0-9]{4,16}$/.test(raw.lay) ? raw.lay : null) : (before?.lay ?? null);
  const plain = has("plain") ? raw.plain === true : before?.plain === true;
  // motion (S7): a transition of its own, and builds of the boxes it still has (a box removed leaves no build)
  const transition = has("transition") ? (isTransition(raw.transition) ? raw.transition : null) : (before?.transition ?? null);
  const builds = cleanBuilds(has("builds") ? raw.builds : before?.builds, boxes.map((b) => b.id));
  const advance = cleanAdvance(has("advance") ? raw.advance : before?.advance);
  // SA: the title's marked words, while they are its words (a new title elsewhere drops them)
  const titleRuns = cleanTitleRuns(has("titleRuns") ? raw.titleRuns : before?.titleRuns, title);
  // SC: the section it starts, hidden when presenting, its master's graphics left off
  const section = has("section") ? cleanSectionName(raw.section) : (before?.section ?? null);
  const hidden = has("hidden") ? raw.hidden === true : before?.hidden === true;
  const noMaster = has("noMaster") ? raw.noMaster === true : before?.noMaster === true;
  return {
    layout,
    title,
    body,
    ...(body2 ? { body2 } : {}),
    ...(figure ? { figure } : {}),
    ...(cite ? { cite } : {}),
    ...(notes ? { notes } : {}),
    ...(image ? { image } : {}),
    ...(widget ? { widget } : {}),
    ...(boxes.length ? { boxes } : {}),
    ...(Object.keys(place).length ? { place } : {}),
    ...(bg ? { bg } : {}),
    ...(lay ? { lay } : {}),
    ...(plain ? { plain: true } : {}),
    ...(transition ? { transition } : {}),
    ...(builds.length ? { builds } : {}),
    ...(advance ? { advance } : {}),
    ...(titleRuns ? { titleRuns } : {}),
    ...(section ? { section } : {}),
    ...(hidden ? { hidden: true } : {}),
    ...(noMaster ? { noMaster: true } : {}),
  };
}

/** A slide's boxes within the limits (shared/slideDesign.ts), or refused with words saying which: checked as it is written. */
export function checkSlide(s: SlideData): SlideData {
  const why = boxLimits(s.boxes ?? []);
  if (why) throw new SlideRefused(why);
  return s;
}

/** A slide's put laid over the slide it changes, as the server lays it (a patch of boxes and places, shared/slideDesign.ts wireSlide). */
export function applySlidePatch(before: SlideData | null, patch: Record<string, unknown>): SlideData {
  return cleanSlide(patch, before);
}

export function cleanDeck(raw: Record<string, unknown>, before: DeckData | null): DeckData {
  const style = isDeckStyle(raw.style) ? raw.style : (before?.style ?? "board");
  const accent = Object.hasOwn(raw, "accent") ? (typeof raw.accent === "string" && HEX.test(raw.accent) ? raw.accent.toUpperCase() : null) : (before?.accent ?? null);
  // a theme of the user's own and their layouts (S6): null clears the theme, a list replaces the layouts
  const theme = Object.hasOwn(raw, "theme") ? cleanDeckTheme(raw.theme) : (before?.theme ?? null);
  let layouts: SavedLayout[];
  try {
    layouts = Object.hasOwn(raw, "layouts") ? cleanLayouts(raw.layouts) : (before?.layouts ?? []);
  } catch (e) {
    if (e instanceof ThemeRefused) throw new SlideRefused(e.message);
    throw e;
  }
  const transition = Object.hasOwn(raw, "transition") ? (isTransition(raw.transition) && raw.transition !== "none" ? raw.transition : null) : (before?.transition ?? null);
  // SC: the slide master (null clears it)
  const master = Object.hasOwn(raw, "master") ? cleanMaster(raw.master) : (before?.master ?? null);
  return { style, accent, ...(theme ? { theme } : {}), ...(layouts.length ? { layouts } : {}), ...(transition ? { transition } : {}), ...(master ? { master } : {}) };
}

/** A slide entry's fields, read whatever was stored. */
export function slideOf(e: Pick<Entry, "data"> | null | undefined): SlideData {
  try {
    return cleanSlide((e?.data ?? {}) as Record<string, unknown>, null);
  } catch {
    return { layout: "text", title: "", body: null };
  }
}

export function deckDataOf(e: Pick<Entry, "data"> | null | undefined): DeckData {
  return cleanDeck((e?.data ?? {}) as Record<string, unknown>, null);
}

/** A board's slides, in their order. */
export function slidesOf<E extends Pick<Entry, "t" | "order" | "createdAt" | "id">>(entries: E[]): E[] {
  return entries.filter((e) => e.t === "slide").sort((a, b) => a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
}

/** A board's deck entry (its style), when it has one. */
export function deckEntryOf<E extends Pick<Entry, "t" | "createdAt">>(entries: E[]): E | null {
  return entries.filter((e) => e.t === "deck").sort((a, b) => a.createdAt - b.createdAt)[0] ?? null;
}

/** A slide's words, a block's or the whole text's, on one line. */
export function wordsOf(nodes: DocNode[] | undefined): string {
  const walk = (n: DocNode): string => (n.type === "text" ? (n.text ?? "") : n.type === "hardBreak" ? " " : (n.content ?? []).map(walk).join(n.type === "listItem" || n.type === "bulletList" || n.type === "orderedList" ? " " : ""));
  return (nodes ?? [])
    .map(walk)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/** A slide's name, for lists and replies: its title, else its figure or its first words (a box's too), else its layout. */
export function slideName(s: SlideData, max = 60): string {
  const t = s.title.replace(/\s+/g, " ").trim() || s.figure || wordsOf(s.body?.content) || headline(s) || (s.layout === "blank" ? "Blank" : "");
  if (!t) return `Untitled ${LAYOUT[s.layout].name.toLowerCase()}`;
  return t.length > max ? t.slice(0, max - 1).trimEnd() + "…" : t;
}

/** A slide's biggest words in a box (its headline, on a slide designed from Blank), the highest first among equals. */
function headline(s: SlideData): string {
  const boxes = (s.boxes ?? []).filter((b) => (b.kind === "text" || b.kind === "shape") && boxWords(b.text));
  boxes.sort((a, b) => (b.size ?? 34) - (a.size ?? 34) || a.y - b.y);
  return boxes[0] ? boxWords(boxes[0].text) : "";
}

/** Whether a slide holds nothing of the user's: no words, picture or widget. */
export function isEmptySlide(s: SlideData): boolean {
  return !s.title && !wordsOf(s.body?.content) && !wordsOf(s.body2?.content) && !s.figure && !s.cite && !s.notes && !s.image && !s.widget && !s.boxes?.length && !s.bg;
}

// ---------------------------------------------------------------------------
// the type scale, at 1600 by 900, on an 8px baseline. The browser sets these as the slide's own variables, and the
// fit estimate below reads the same numbers, so the board and an agent's reply agree.

export const SLIDE_W = 1600;
export const SLIDE_H = 900;
/** the side margin (7% of the width) and the top and bottom one */
export const MARGIN_X = 112;
export const MARGIN_Y = 96;

export interface TypeStep {
  size: number;
  line: number;
}

export const TYPE = {
  hero: { size: 104, line: 112 },
  section: { size: 88, line: 96 },
  title: { size: 56, line: 64 },
  sub: { size: 36, line: 48 },
  body: { size: 34, line: 48 },
  col: { size: 30, line: 40 },
  side: { size: 28, line: 40 },
  quote: { size: 56, line: 72 },
  figure: { size: 248, line: 248 },
  meaning: { size: 40, line: 56 },
  small: { size: 24, line: 32 },
  full: { size: 64, line: 72 },
} satisfies Record<string, TypeStep>;

/** the smallest a slide's text shrinks to before it is cut off */
export const MIN_FIT = 0.4;
/** below this, an agent is told the slide holds too much */
export const TOO_MUCH = 0.85;

/** How wide a line of words is, as a share of the type's size: Geist's average letter, with the space between words. */
const ADVANCE = 0.5;

/** The lines a run of words takes in a box, wrapped at word breaks (roughly: by its letters' average width). */
export function linesFor(words: string, width: number, size: number): number {
  const perLine = Math.max(1, Math.floor(width / (size * ADVANCE)));
  let lines = 0;
  for (const part of words.split("\n")) {
    const w = part.trim().split(/\s+/).filter(Boolean);
    if (!w.length) {
      lines++;
      continue;
    }
    let n = 1;
    let used = 0;
    for (const word of w) {
      const len = word.length;
      if (used && used + 1 + len > perLine) {
        n++;
        used = len;
      } else used += (used ? 1 : 0) + len;
      if (len > perLine) n += Math.floor(len / perLine);
    }
    lines += n;
  }
  return Math.max(1, lines);
}

/** How tall a slide's text is in a box of this width, at a step of the type scale times fit. */
function textHeight(nodes: DocNode[] | undefined, width: number, step: TypeStep, fit: number): number {
  const size = step.size * fit;
  const line = step.line * fit;
  const gap = 16 * fit;
  let h = 0;
  let first = true;
  const block = (n: DocNode, indent: number) => {
    if (n.type === "bulletList" || n.type === "orderedList") {
      for (const it of n.content ?? []) for (const c of it.content ?? []) block(c, indent + 1);
      return;
    }
    const nested = indent > 1;
    const s = nested ? size * 0.88 : size;
    const l = nested ? line * 0.88 : line;
    const words = wordsOf([n]);
    if (!first) h += gap;
    first = false;
    h += linesFor(words, width - indent * 44 * fit, s) * l;
  };
  for (const n of nodes ?? []) block(n, 0);
  return h;
}

/** How tall a title is, wrapped in its box. */
const titleHeight = (title: string, width: number, step: TypeStep, fit: number) => (title ? linesFor(title, width, step.size * fit) * step.line * fit : 0);

/**
 * How far a slide's text has to shrink to fit its layout, as the board draws it: 1 when it fits as it is, down to
 * MIN_FIT; below MIN_FIT (by how much more it would need) when even that cuts it off. An estimate from its words, for
 * an agent's reply; the browser measures the real thing.
 */
export function estimateFit(s: SlideData): number {
  const W = SLIDE_W - MARGIN_X * 2;
  const H = SLIDE_H - MARGIN_Y * 2;
  const body = s.body?.content;
  const fits = (fit: number): boolean => {
    switch (s.layout) {
      case "title":
        return 46 + titleHeight(s.title, W, TYPE.hero, fit) + (body?.length ? 32 * fit + textHeight(body, 1100, TYPE.sub, fit) : 0) <= H;
      case "section":
        return 72 + titleHeight(s.title, W, TYPE.section, fit) + (body?.length ? 24 * fit + textHeight(body, 1100, TYPE.col, fit) : 0) <= H;
      case "text":
        return 38 + titleHeight(s.title, W, TYPE.title, fit) + 48 * fit + textHeight(body, 1240, TYPE.body, fit) <= H;
      case "two": {
        const top = 38 + titleHeight(s.title, W, TYPE.title, fit) + 48 * fit;
        return top + Math.max(textHeight(body, (W - 96) / 2, TYPE.col, fit), textHeight(s.body2?.content, (W - 96) / 2, TYPE.col, fit)) <= H;
      }
      case "big":
        return (s.title ? (TYPE.small.line + 24) * fit : 0) + TYPE.figure.line * Math.min(1, figureFit(s.figure ?? "")) * fit + 32 * fit + textHeight(body, 1100, TYPE.meaning, fit) <= H;
      case "quote":
        return (s.title ? (TYPE.small.line + 48) * fit : 0) + 96 * fit + textHeight(body, 1240, TYPE.quote, fit) + (s.cite ? (40 + TYPE.small.line) * fit : 0) <= H;
      case "picture":
        return 38 + titleHeight(s.title, 608, { size: 48, line: 56 }, fit) + 40 * fit + textHeight(body, 608, TYPE.side, fit) <= H;
      case "full":
        return titleHeight(s.title, 1200, TYPE.full, fit) + (body?.length ? 16 * fit + textHeight(body, 1100, TYPE.side, fit) : 0) <= H * 0.6;
      case "widget":
        return 38 + titleHeight(s.title, W, TYPE.title, fit) <= 200;
      default:
        return true;
    }
  };
  if (fits(1)) return 1;
  for (let f = 0.98; f >= MIN_FIT - 1e-9; f -= 0.02) if (fits(f)) return Math.round(f * 100) / 100;
  for (let f = MIN_FIT - 0.02; f > 0.1; f -= 0.02) if (fits(f)) return Math.round(f * 100) / 100;
  return 0.1;
}

/** How far a Big number's figure shrinks to stay on one line across the slide. */
export function figureFit(figure: string): number {
  const len = [...figure].length;
  if (!len) return 1;
  // figures are numbers: tabular figures are about 0.6 of the type's size, a narrow sign less
  const width = len * TYPE.figure.size * 0.6;
  return Math.min(1, (SLIDE_W - MARGIN_X * 2) / width);
}

/** A slide's place in its deck, as people count it: from 1. */
export function slideNumber(slides: Pick<Entry, "id">[], id: string): number {
  return slides.findIndex((s) => s.id === id) + 1;
}

/** Whether a deck holds anything of the user's: more than the one starter slide a new deck opens on, or that slide written in. */
export function deckHasWork(entries: Entry[], boardName: string): boolean {
  const slides = slidesOf(entries);
  if (slides.length > 1) return true;
  if (!slides.length) return false;
  const s = slideOf(slides[0]);
  if (isEmptySlide(s)) return false;
  return !isStarterSlide(s, boardName);
}

/** A slide as a new deck starts with it: a Title slide with the board's name and nothing else. */
export function isStarterSlide(s: SlideData, boardName: string): boolean {
  return s.layout === "title" && s.title.trim() === boardName.trim() && !s.body && !s.notes && !s.image && !s.widget && !s.boxes?.length && !s.place && !s.bg;
}

/**
 * A widget as it is copied onto a deck, by an agent's write_slides or by the user (a Widget slide's "Use one from another
 * board", a slide duplicated): never a schedule or a task to run (`props.auto`), whatever its kind. Only the user sets one
 * (SOP §4), and a second brief or job would run it twice. A Button keeps its words as its task, which waits on the user's
 * yes before it runs, as one from a board file does (arrivedButton).
 */
export function copiedWidget<T extends { kind: string; type?: string; props?: Record<string, unknown> }>(w: T): T {
  const b = arrivedButton(w);
  if (!b.props || !("auto" in b.props)) return b;
  const { auto: _auto, ...props } = b.props;
  return { ...b, props };
}
